/**
 * Expense TanStack Query Hooks (T169)
 * Data fetching and mutations for expense management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient, { type ApiResponse } from '../lib/api-client';

// Types

export interface Expense {
  id: string;
  amount: number;
  amountFormatted: string;
  date: string;
  vendor: string | null;
  category: string;
  categoryLabel: string;
  paymentMethod: string | null;
  description: string;
  notes: string | null;
  receiptUrl: string | null;
  hasReceipt: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateExpenseInput {
  amount: number;
  date: string;
  vendor: string;
  category: string;
  paymentMethod?: string;
  description: string;
  notes?: string;
  receiptUrl?: string;
}

export interface UpdateExpenseInput {
  amount?: number;
  date?: string;
  vendor?: string;
  category?: string;
  paymentMethod?: string;
  description?: string;
  notes?: string;
  receiptUrl?: string;
}

export interface ExpenseFilters {
  category?: string;
  vendor?: string;
  paymentMethod?: string;
  dateFrom?: string;
  dateTo?: string;
  search?: string;
  sort?: string;
  order?: string;
  page?: number;
  limit?: number;
}

export interface ExpensesResponse {
  data: Expense[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  summary: {
    grandTotal: number;
    grandTotalFormatted: string;
    byCategory: Record<string, number>;
  };
}

export interface ExpenseReportData {
  dateFrom: string;
  dateTo: string;
  grandTotal: number;
  grandTotalFormatted: string;
  count: number;
  byCategory: Array<{
    category: string;
    categoryLabel: string;
    count: number;
    total: number;
    totalFormatted: string;
    percentage: number;
  }>;
  byMonth: Array<{
    month: string;
    count: number;
    total: number;
    totalFormatted: string;
  }>;
  topVendors: Array<{
    vendor: string;
    count: number;
    total: number;
    totalFormatted: string;
  }>;
}

// Query hooks

export function useExpenses(filters?: ExpenseFilters) {
  return useQuery({
    queryKey: [
      'expenses',
      filters?.category,
      filters?.vendor,
      filters?.paymentMethod,
      filters?.dateFrom,
      filters?.dateTo,
      filters?.search,
      filters?.sort,
      filters?.order,
      filters?.page,
    ],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.category) {
        params.append('category', filters.category);
      }
      if (filters?.vendor) {
        params.append('vendor', filters.vendor);
      }
      if (filters?.paymentMethod) {
        params.append('paymentMethod', filters.paymentMethod);
      }
      if (filters?.dateFrom) {
        params.append('startDate', filters.dateFrom);
      }
      if (filters?.dateTo) {
        params.append('endDate', filters.dateTo);
      }
      if (filters?.search) {
        params.append('search', filters.search);
      }
      if (filters?.sort) {
        params.append('sortBy', filters.sort);
      }
      if (filters?.order) {
        params.append('sortOrder', filters.order);
      }
      if (filters?.page) {
        params.append('page', filters.page.toString());
      }
      if (filters?.limit) {
        params.append('limit', filters.limit.toString());
      }

      const response = await apiClient.get<ApiResponse<ExpensesResponse>>(
        `/expenses?${params.toString()}`
      );
      return response.data.data;
    },
  });
}

export function useExpense(id: string) {
  return useQuery({
    queryKey: ['expense', id],
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<Expense>>(`/expenses/${id}`);
      return response.data.data;
    },
    enabled: !!id,
  });
}

export function useExpenseReport(dateFrom: string, dateTo: string) {
  return useQuery({
    queryKey: ['expenses', 'report', dateFrom, dateTo],
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<ExpenseReportData>>(
        `/expenses/report?dateFrom=${dateFrom}&dateTo=${dateTo}`
      );
      return response.data.data;
    },
    enabled: !!dateFrom && !!dateTo,
  });
}

export function useExpenseCategories() {
  return useQuery({
    queryKey: ['expenses', 'categories'],
    queryFn: async () => {
      const response = await apiClient.get<
        ApiResponse<{
          categories: Array<{
            category: string;
            categoryLabel: string;
            count: number;
            total: number;
          }>;
        }>
      >('/expenses/categories/list');
      return response.data.data;
    },
    staleTime: 300_000,
  });
}

// Mutation hooks

export function useCreateExpense() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateExpenseInput) => {
      const response = await apiClient.post<ApiResponse<Expense>>('/expenses', data);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useUpdateExpense() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateExpenseInput }) => {
      const response = await apiClient.put<ApiResponse<Expense>>(`/expenses/${id}`, data);
      return response.data.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['expense', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useDeleteExpense() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/expenses/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useExportExpenses() {
  return useMutation({
    mutationFn: async ({
      dateFrom,
      dateTo,
      format = 'csv',
    }: {
      dateFrom: string;
      dateTo: string;
      format?: 'csv' | 'pdf';
    }) => {
      const response = await apiClient.get(
        `/expenses/export?dateFrom=${dateFrom}&dateTo=${dateTo}&format=${format}`,
        { responseType: 'blob' }
      );
      return {
        blob: response.data as Blob,
        filename: `expenses-${dateFrom}-to-${dateTo}.${format}`,
      };
    },
    onSuccess: ({ blob, filename }) => {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
  });
}
